import React, { useState } from 'react'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { useCompanyMutationsQuery } from 'hooks/api'

import { Stack, Typography } from '@mui/material'

import { Modal, Button, Input, TextArea } from '@repo/ui'
import { SelectField } from 'components'
import type { CompanyUser } from 'types'

const SUSPEND_MEMBER_REASONS = [
  { label: 'Abuse', value: 'Abuse' },
  { label: 'Security concern', value: 'Security concern' },
  { label: 'Policy violation', value: 'Policy violation' },
  { label: 'Other', value: 'Other' },
]

interface SuspendMemberModalProps {
  member: CompanyUser | null
  companyId: string
  companyName?: string
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export const SuspendMemberModal = ({
  member,
  companyId,
  companyName,
  open,
  onClose,
  onSuccess,
}: SuspendMemberModalProps) => {
  const { open: notification } = useSnackbar()

  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')

  const { suspendMember, isLoading } = useCompanyMutationsQuery({
    onSuccess: () => {
      const memberName = member
        ? `${member.first_name} ${member.last_name}`
        : 'Member'
      handleClose()
      onSuccess()
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      notification({
        type: 'error',
        title: message,
      })
    },
  })

  const handleSuspend = () => {
    if (!member?.id || !companyId) return
    const fullReason = reason
      ? note.trim()
        ? `${reason} — ${note.trim()}`
        : reason
      : undefined
    suspendMember({ companyId, userId: member.id, reason: fullReason })
  }

  const handleClose = () => {
    setReason('')
    setNote('')
    onClose()
  }

  const memberName = member
    ? `${member.first_name} ${member.last_name}`
    : 'this member'

  return (
    <Modal
      title="Suspend access"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            disableElevation
            destructive
            onClick={handleSuspend}
            disabled={isLoading || !reason}
          >
            {isLoading ? 'Suspending...' : 'Suspend'}
          </Button>
        </>
      }
    >
      <Stack spacing="16px">
        <Typography variant="body2" color="text.secondary">
          This blocks{' '}
          <strong>{memberName}</strong>&apos;s access to{' '}
          <strong>{companyName ?? 'this company'}</strong>. Other members are
          unaffected. Reversible via Unsuspend.
        </Typography>

        <SelectField
          label="Reason"
          value={reason}
          onChange={setReason}
          options={SUSPEND_MEMBER_REASONS}
        />

        <TextArea
          label="Note (optional)"
          placeholder="Add additional context…"
          value={note}
          onValueChange={setNote}
          minRows={3}
        />
      </Stack>
    </Modal>
  )
}
